import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useAction } from "../../../../hooks/useAction";
import {
  addPx,
  analyticsEventTypes,
  generateProductPrices,
  parseCssStringToObject,
  replacePlaceholderFromParentData,
  resolveBlockRotate,
  buildFontFamilyStyle,
  resolvePreviewIsMobile,
} from "../../../../helper";
import { formatStorefrontMoney, formatStorefrontMoneyFromCents } from "../../../../utils/format-storefront-money";
import { useConfetti } from "../../../../hooks/useConfetti";
import Loader from "../Loader";
import {
  DEFAULT_BLOCK_CSS,
  POPUP_SECTIONS,
  TEMPLATE_TYPE,
} from "../../../../constant/constant";
import { useApp } from "../../../../context/AppState";
import { usePopupFormContext } from "../../../../hooks/usePopupFormContext";
import { useFormSumbmit } from "../../../../hooks/useFormSubmit";
import ImageBlock from "../../../ImageBlock/ImageBlock";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { trackClick, trackConversion } from "../../../../utils/analyticsUtils";
import { getActiveStorefrontTrackingContext } from "../../../../services/cart.service";
import { isStaticCustomCartDrawer } from "../../../../constants/custom-cart-drawer-template";
import { getStepSectionsForPopup } from "../../../../utils/popup-step-sections";
import {
  ensureProductUpsellAddToCartButtonSettings,
  looksLikeProductAddToCartButton,
} from "../../../../../shared/product-upsell-recommendation-defaults.js";
import { isSectionVisibleForViewport } from "../../../../../shared/section-visibility.js";

const BUTTON_TEXT_STYLE_KEYS = new Set([
  "fontFamily",
  "rotation",
  "fontSize",
  "color",
  "fontWeight",
  "lineHeight",
  "letterSpacing",
  "textAlign",
  "marginBottom",
  "marginBottomPx",
]);

const splitButtonTypographyCss = (css = {}) => {
  const typography = {};
  const chrome = {};

  Object.entries(css || {}).forEach(([key, value]) => {
    if (BUTTON_TEXT_STYLE_KEYS.has(key)) {
      typography[key] = value;
    } else {
      chrome[key] = value;
    }
  });

  return { typography, chrome };
};

const mergeTypographyCss = (base = {}, override = {}) => ({
  ...base,
  ...Object.fromEntries(
    Object.entries(override).filter(([, value]) => value != null && value !== "")
  ),
});

ButtonBlock.propTypes = {
  section: PropTypes.object,
};

export default function ButtonBlock({
  popupData,
  setPopupState,
  popupState,
  section,
  handleClose,
  index,
  productIndex,
  productData,
  parentData,
  compactAddedLabel = false,
}) {
  const {
    isMobile: viewportIsMobile,
    previewDeviceIsMobile,
    cartData,
    appState,
    setCartOpen,
  } = useApp();
  const isMobile = resolvePreviewIsMobile(
    appState,
    viewportIsMobile,
    previewDeviceIsMobile
  );
  const activeStep = useMemo(() => popupState?.activeStep, [popupState]);
  const { getCouponCode, addCart, upgradeOrder, checkout } = useFormSumbmit({
    popupData,
    setPopupState,
    index,
    productIndex,
    productData,
    handleClose,
    activeStep,
  });
  const {
    trigger,
    reset,
    watch,
    formState: { errors },
  } = usePopupFormContext();
  const { actionHandler } = useAction();
  const { addAnalyticsEvent } = useAnalytics();

  const [isLoading, setIsLoading] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  const disabled = false;

  const popupType = useMemo(() => popupData?.popUpType, [popupData]);
  const popupId = useMemo(() => popupData?._id, [popupData]);

  const popupSettings = popupData?.schema[activeStep]?.popupSettings;
  const buttonSection = useMemo(
    () => ensureProductUpsellAddToCartButtonSettings({ ...section }),
    [section]
  );
  const { type: actionType, parameters: actionParameters } =
    buttonSection?.setting?.action || {};

  const isPrimaryCatalogCta = buttonSection?.setting?.role === "primaryCta";
  const isAddToCartFormEvent =
    actionType === "form_submit" &&
    [
      "product_add_cart",
      "product_add_cart_close_popup",
      "product_add_cart_refresh_the_window",
      "product_add_cart_open_the_custom_cart_drawer",
    ].includes(actionParameters?.event);
  const isAddToCartAction =
    actionType === "add_to_cart" ||
    isAddToCartFormEvent ||
    isPrimaryCatalogCta ||
    looksLikeProductAddToCartButton(buttonSection);

  const { type: btnType } = buttonSection.setting?.btn_type || {};
  const btnIcon = buttonSection.setting?.btn_type?.icon?.image || null;
  const effectiveBtnType =
    btnType || (btnIcon ? "text_with_icon" : "text");
  const buttonText = (() => {
    const raw = buttonSection?.text;
    if (typeof raw === "string") return raw.trim();
    if (raw && typeof raw === "object") {
      return String(raw.html || raw.text || "").trim();
    }
    return "";
  })();
  const resolveIconDimension = (css, sizeKey, unitKey, fallback) => {
    const value = css?.[sizeKey];
    if (value == null || value === "") return fallback;
    const unit = css?.[unitKey] || "px";
    if (unit === "%") return `${value}%`;
    return addPx(value);
  };

  const viewportButtonCssForIcon =
    buttonSection?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"] || {};
  const resolvedIconWidth = resolveIconDimension(
    viewportButtonCssForIcon,
    "iconWidth",
    "iconWidthUnit",
    null
  );
  const resolvedIconHeight = resolveIconDimension(
    viewportButtonCssForIcon,
    "iconHeight",
    "iconHeightUnit",
    null
  );
  const btnIconWidth =
    resolvedIconWidth ||
    viewportButtonCssForIcon?.fontSize ||
    "16px";
  const btnIconHeight = resolvedIconHeight || btnIconWidth;

  const viewportButtonCss =
    buttonSection?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"] || {};
  const overrideButtonCss = parseCssStringToObject(
    buttonSection?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
  );
  const { typography: viewportTypography, chrome: viewportButtonChromeCss } =
    splitButtonTypographyCss(viewportButtonCss);
  const { typography: overrideTypography, chrome: overrideButtonChromeCss } =
    splitButtonTypographyCss(overrideButtonCss);
  const buttonTypography = mergeTypographyCss(
    viewportTypography,
    overrideTypography
  );
  const resolvedButtonRotate = resolveBlockRotate(buttonTypography.rotation);
  const isCompactInlineCta =
    viewportButtonCss.width === "auto" ||
    viewportButtonCss.width === "max-content";

  const isCartDrawerUpsellCta =
    compactAddedLabel || isStaticCustomCartDrawer(popupData);
  const addedSuccessLabel = isCartDrawerUpsellCta ? "Added" : "Added to cart";
  const addedIconSize = isCartDrawerUpsellCta ? 14 : 20;
  const addedGap = isCartDrawerUpsellCta ? "4px" : "10px";
  const addedFontSize = isCartDrawerUpsellCta ? "12px" : undefined;

  const blockCSS = {
    ...DEFAULT_BLOCK_CSS,
    ...(isCompactInlineCta
      ? { width: "auto", height: "auto", minHeight: 36, maxHeight: "none" }
      : {}),
    ...viewportButtonChromeCss,
    ...overrideButtonChromeCss,
    borderRadius: addPx(
      buttonSection?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"]
        ?.borderRadius
    ),
    display:
      viewportButtonChromeCss.display === "none" ||
      overrideButtonChromeCss.display === "none"
        ? "none"
        : "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: buttonTypography.textAlign || "center",
    opacity: isLoading ? 0.7 : 1,
    ...(!isLoading && { cursor: "pointer" }),
    pointerEvents: appState?.is_preview_mode ? "none" : "auto",
    ...(section?.setting?.layoutFlow
      ? {
          position: "relative",
          top: "auto",
          left: "auto",
          right: "auto",
          bottom: "auto",
          width: "100%",
          height: "auto",
          minHeight: 44,
          maxHeight: 48,
        }
      : {}),
  };

  const btnTextIconBlockStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    width: isCompactInlineCta ? "auto" : "100%",
    height: "auto",
    flexWrap: "nowrap",
    whiteSpace: "nowrap",
    background: "transparent",
    backgroundColor: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    transformOrigin: "center center",
    rotate: resolvedButtonRotate,
    ...buildFontFamilyStyle(buttonTypography.fontFamily),
    ...(buttonTypography.fontSize ? { fontSize: buttonTypography.fontSize } : {}),
    ...(buttonTypography.color ? { color: buttonTypography.color } : { color: "inherit" }),
    ...(buttonTypography.fontWeight
      ? { fontWeight: buttonTypography.fontWeight }
      : { fontWeight: "inherit" }),
    ...(buttonTypography.lineHeight
      ? { lineHeight: buttonTypography.lineHeight }
      : { lineHeight: "inherit" }),
    ...(buttonTypography.letterSpacing
      ? { letterSpacing: buttonTypography.letterSpacing }
      : {}),
    ...(buttonTypography.textAlign ? { textAlign: buttonTypography.textAlign } : {}),
  };

  const confetti = useConfetti(popupData);

  if (!buttonText && effectiveBtnType !== "icon") return null;
  if (effectiveBtnType === "icon" && !btnIcon && !buttonText) return null;

  const handleOnClick = async (e) => {
    if (appState?.is_preview_mode) {
      return;
    }

    let formData = {};

    const form = e.target.closest("form");

    if (form) {
      const tempFormData = new FormData(form);
      formData = Object.fromEntries(tempFormData?.entries());
    }

    setIsLoading(true);

    if (disabled) {
      setIsLoading(false);
      return;
    }

    if (actionType !== "close_popup") {
      void trackClick({
        popupId: popupData?._id,
        popupType: popupData?.popUpType || popupData?.template_id?.title,
        campaignId: popupData?.campaign_Id,
        clickTarget: "button",
        actionType: actionType || "button",
        productId:
          productData?.id ??
          productData?.admin_graphql_api_id ??
          parentData?.id ??
          parentData?.admin_graphql_api_id,
        addAnalyticsEvent,
      });
    }

    const finishAddToCartUi = (e, success) => {
      if (!success) return;
      setAddedToCart(true);
      if (isPrimaryCatalogCta && typeof handleClose === "function") {
        window.setTimeout(() => handleClose(e), 1000);
      }
    };

    switch (actionType) {
      case "go_to_step": {
        const { openStep } = actionParameters;
        confetti.fire();
        const stateKey = `${popupType}_${popupId}`;
        let targetStep = Number(openStep);
        if (!Number.isFinite(targetStep)) targetStep = 0;

        if (isStaticCustomCartDrawer(popupData)) {
          setCartOpen(true);
          await manageAnalytics();
          break;
        }

        setPopupState((prev) => ({
          ...prev,
          [stateKey]: {
            ...(prev[stateKey] || {}),
            activeStep: targetStep,
            openPopup: true,
          },
        }));
        await manageAnalytics();
        break;
      }
      case "open_link":
        const { link } = actionParameters;
        const { new_tab, close_after, is_conversation } =
          section?.setting?.action ?? {};

        if (link) {
          if (is_conversation) {
            await manageAnalytics();
          }

          const finalLink = replacePlaceholderFromParentData(link, parentData);

          if (new_tab) {
            window.open(finalLink, "_blank");
          } else {
            window.location.href = finalLink;
          }
        }
        if (close_after) {
          handleClose(e);
        }
        break;
      case "form_submit":
        const { event, openStep: step } = actionParameters;
        const { popUpType } = popupData;
        const isCustomCart = popUpType === TEMPLATE_TYPE?.CUSTOM_CART ? true : false;        
        const isValid = await trigger();
        if (isValid) {
          let shouldResetForm = true;

          switch (event) {
            case "get_coupon_code": {
              const result = await getCouponCode(step);
              shouldResetForm = Boolean(result?.ok);
              break;
            }
            case "product_add_cart": {
              const success = await addCart(
                formData,
                false,
                false,
                isCustomCart,
                true,
                compactAddedLabel
              );
              finishAddToCartUi(e, success);
              break;
            }
            case "product_add_cart_close_popup":
              await addCart(formData, false, false, isCustomCart);
              break;
            case "product_add_cart_refresh_the_window":
              await addCart(formData, true);
              break;
            case "product_upgrade_order":
              await upgradeOrder(formData);
              break;
            case "product_check_out":
              await checkout(formData, false, {
                buyNowAction: actionParameters?.buyNowAction,
                clickEvent: e,
              });
              break;
            case "product_add_cart_open_the_custom_cart_drawer":
               await addCart(formData,false,true);
              break;
            case "check_out_with_price":
               await checkout(cartData, true);
              break;
            default:
              break;
          }

          await manageAnalytics();
          if (shouldResetForm) {
            reset();
          }
        }

        break;
      case "add_to_cart": {
        const success = await addCart(
          formData,
          false,
          false,
          false,
          true,
          compactAddedLabel
        );
        if (success) {
          finishAddToCartUi(e, true);
          await manageAnalytics();
        }
        break;
      }
      case "close_popup":
        handleClose(e);
        reset();
        break;
      default:
        if (isAddToCartAction) {
          const { popUpType } = popupData;
          const isCustomCart = popUpType === TEMPLATE_TYPE?.CUSTOM_CART;
          const isValid = await trigger();
          if (isValid) {
            const success = await addCart(
              formData,
              false,
              false,
              isCustomCart,
              true,
              compactAddedLabel
            );
            finishAddToCartUi(e, success);
            await manageAnalytics();
          }
        }
        break;
    }

    setIsLoading(false);
  };

  const manageAnalytics = async () => {
    if (buttonSection?.setting?.isConversationEnabled === false) {
      return;
    }

    // Pixel-tracked storefront upsells record conversion+revenue on orders/paid webhook.
    if (getActiveStorefrontTrackingContext()) {
      return;
    }

    await trackConversion({
      popupId: popupData._id,
      popupType: popupData.template_id?.title,
      campaignId: popupData.campaign_Id,
      addAnalyticsEvent,
    });
  };
  
  const productsArray = getStepSectionsForPopup(popupData, activeStep)
    .filter((section) => section?.type === POPUP_SECTIONS.PRODUCT)
    .flatMap((section) => section?.products || []);

  const selectedVariantIds = productsArray?.map((_, index) =>
    watch(`${POPUP_SECTIONS.VARIANT_PICKER}-${index}`)
  );

  const productPriceData = generateProductPrices({
    products: productsArray,
    selectedVariantIds: selectedVariantIds,
    currency: window?.Shopify?.currency?.active,
    isPreviewMode: appState?.is_preview_mode,
    shopCurrency: appState?.shop?.currency,
  });

  const getFinalPrice = () => {
    const { popUpType } = popupData || {};

    switch (popUpType) {
      case TEMPLATE_TYPE.CUSTOM_CART:
        return formatStorefrontMoneyFromCents(cartData?.total_price, appState);
      default: {
        const discounted = Number(productPriceData?.totals?.discounted_price);
        if (!Number.isFinite(discounted)) {
          return formatStorefrontMoney(0, appState);
        }
        return formatStorefrontMoney(discounted, appState);
      }
    }
  };

  if (!isSectionVisibleForViewport(section, isMobile)) {
    return null;
  }

  return (
    <>
      {effectiveBtnType === "icon" ? (
        btnIcon && (
          <div
            className={
              isAddToCartAction
                ? "weupsell-btn-icon-wrap weupsell_add_cart_button"
                : "weupsell-btn-icon-wrap"
            }
            data-weupsell-add-cart-button={
              isAddToCartAction ? "true" : undefined
            }
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              boxSizing: "border-box",
              pointerEvents: appState?.is_preview_mode ? "none" : "auto",
              cursor: appState?.is_preview_mode ? "default" : undefined,
              backgroundColor:
                section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"]
                  ?.backgroundColor || "transparent",
              borderRadius: addPx(
                section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"]
                  ?.borderRadius
              ),
            }}
            onClick={(e) => {
              if (appState?.is_preview_mode) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              actionHandler(() => handleOnClick(e));
            }}
          >
            <img
              src={btnIcon}
              alt=""
              draggable={false}
              style={{
                width: resolvedIconWidth || "100%",
                height: resolvedIconHeight || "100%",
                objectFit: "contain",
                objectPosition: "center",
                display: "block",
                flexShrink: 0,
              }}
            />
          </div>
        )
      ) : (
        <button
          type="button"
          className={isAddToCartAction ? "weupsell_add_cart_button" : undefined}
          data-weupsell-add-cart-button={
            isAddToCartAction ? "true" : undefined
          }
          style={blockCSS}
          onMouseDown={(e) => {
            if (appState?.is_preview_mode) e.stopPropagation();
          }}
          onClick={(e) => {
            if (appState?.is_preview_mode) {
              e.stopPropagation();
              return;
            }
            actionHandler(() => handleOnClick(e));
          }}
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <Loader />
          ) : (
            <>
              {effectiveBtnType === "text" && (
                <div style={btnTextIconBlockStyle}>
                  {addedToCart && (
                    <svg
                      width={14}
                      height={14}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                      style={{ flexShrink: 0 }}
                    >
                      <circle cx="12" cy="12" r="12" fill="#22c55e" />
                      <path
                        d="M7 12.5l3.25 3.25L17 9"
                        stroke="#ffffff"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  )}
                  {buttonText}
                  {actionParameters?.event === "check_out_with_price" && (
                    <span style={{ marginLeft: "2px" }}>{getFinalPrice()}</span>
                  )}
                </div>
              )}
              {effectiveBtnType === "text_with_icon" && (
                <div style={btnTextIconBlockStyle}>
                  {addedToCart && (
                    <svg
                      width={14}
                      height={14}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                      style={{ flexShrink: 0 }}
                    >
                      <circle cx="12" cy="12" r="12" fill="#22c55e" />
                      <path
                        d="M7 12.5l3.25 3.25L17 9"
                        stroke="#ffffff"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  )}
                  {buttonText}
                  {btnIcon && (
                    <img
                      src={btnIcon}
                      alt="btn Icon"
                      style={{
                        width: btnIconWidth,
                        height: btnIconHeight,
                        flexShrink: 0,
                      }}
                      draggable={false}
                    />
                  )}
                </div>
              )}
            </>
          )}
          <style>{`
          button:hover {
            opacity: 0.95;
            box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
          }
          button:disabled {
            cursor: not-allowed;
          }
        `}</style>
        </button>
      )}
    </>
  );
}
